import { useState } from "react";
import { Link } from "react-router-dom";
import type { JourneyDayTotals, TrafficKind } from "@starsach/shared-types";
import { useConcurrency, useContactRecords, useJourneyOverview, useQuotas } from "../../api/hooks";
import { OUTCOME_SERIES, StackedBars } from "../../components/StackedBars";
import { Hint } from "../../components/Hint";
import { Card, EmptyState, ErrorState, LoadingState, RelativeTime, StatusPill, surface } from "../../components/ui";
import type { TooltipId } from "../../lib/tooltips";
import { useFormat } from "../../lib/useFormat";
import { OUTCOME_LABEL, OUTCOME_TONE } from "./outcomes";
import { TestPill, TestTrafficFigure } from "./TestTraffic";

type Days = 7 | 30 | 90;

/**
 * Journeys · Overview (requirements 8.4, the mock-up of 2026-09-18): AI
 * and human agents on one scorecard. Every number is a day bucket sum,
 * so 90 days costs 180 rows; the deltas are against the window before.
 * #448: real traffic by default; test traffic is its own labeled figure,
 * and can be viewed alone.
 */
export function OverviewSection({ environmentId, envName, canInspect }: { environmentId: string; envName: string; canInspect: boolean }) {
  const [days, setDays] = useState<Days>(30);
  const [traffic, setTraffic] = useState<TrafficKind>("real");
  const q = useJourneyOverview(environmentId, days, traffic);
  const fmt = useFormat();

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-neutral-600 dark:text-neutral-400">AI and human agents on one scorecard · {envName}</p>
        <div role="group" aria-label="Window" className="inline-flex overflow-hidden rounded-lg border border-neutral-300 text-sm dark:border-neutral-700">
          {([7, 30, 90] as Days[]).map((d) => (
            <button key={d} type="button" aria-pressed={days === d} onClick={() => setDays(d)} className={`min-h-9 px-3 ${days === d ? "bg-accent-soft font-semibold text-accent-strong dark:bg-neutral-800 dark:text-white" : "bg-white text-neutral-700 hover:bg-neutral-50 dark:bg-neutral-900 dark:text-neutral-300"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>
      {q.isPending && <LoadingState label="Loading the scorecard" />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.data && !q.data.available && (
        <EmptyState
          title="No contact records yet"
          hint="The scorecard is built from one record per contact, sent by this environment's stack with EnableContactRecords (ADR-0015). Until the first record lands there is nothing to count; paths and concurrency still work without it."
        />
      )}
      {q.data && q.data.available && (
        <>
          <TestTrafficFigure count={q.data.testContacts ?? 0} viewing={traffic === "test"} onToggle={(test) => setTraffic(test ? "test" : "real")} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Tile label="Contacts" value={fmt.number(q.data.totals.contacts)} delta={pctDelta(q.data.totals.contacts, q.data.prior.contacts)} deltaUnit="%" />
            <Tile label="Contained" hint="journeys.contained" value={pct(q.data.totals.contained, q.data.totals.contacts)} delta={ptsDelta(rate(q.data.totals.contained, q.data.totals.contacts), rate(q.data.prior.contained, q.data.prior.contacts))} good="up" />
            <Tile label="Escalated to agent" hint="journeys.escalated" value={pct(q.data.totals.answered, q.data.totals.contacts)} delta={ptsDelta(rate(q.data.totals.answered, q.data.totals.contacts), rate(q.data.prior.answered, q.data.prior.contacts))} />
            <Tile label="Abandoned in queue" hint="journeys.abandoned" value={pct(q.data.totals.abandoned, q.data.totals.contacts)} delta={ptsDelta(rate(q.data.totals.abandoned, q.data.totals.contacts), rate(q.data.prior.abandoned, q.data.prior.contacts))} good="down" tone={rate(q.data.totals.abandoned, q.data.totals.contacts) > 0.05 ? "warning" : "neutral"} />
            <Tile label="Repeat within 7d" hint="journeys.repeat" value={pct(q.data.totals.repeat7d, q.data.totals.contacts)} delta={ptsDelta(rate(q.data.totals.repeat7d, q.data.totals.contacts), rate(q.data.prior.repeat7d, q.data.prior.contacts))} good="down" />
          </div>
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            <Card title="How contacts ended, by day">
              <StackedBars rows={q.data.byDay} series={OUTCOME_SERIES} label={`Contact outcomes by day for the last ${days} days`} formatX={(d) => fmt.day(d)} />
              <Waits totals={q.data.totals} />
            </Card>
            <div className="grid gap-5 self-start">
              <ConcurrencyCard environmentId={environmentId} />
              <RecentContacts environmentId={environmentId} canInspect={canInspect} traffic={traffic} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const rate = (n: number, d: number) => (d ? n / d : 0);
const pct = (n: number, d: number) => `${(rate(n, d) * 100).toFixed(1)}%`;
const pctDelta = (now: number, before: number) => (before ? ((now - before) / before) * 100 : undefined);
const ptsDelta = (now: number, before: number) => (before || now ? (now - before) * 100 : undefined);

function Tile({ label, hint, value, delta: raw, deltaUnit = " pts", good, tone = "neutral" }: { label: string; hint?: TooltipId; value: string; delta: number | undefined; deltaUnit?: string; good?: "up" | "down"; tone?: "neutral" | "warning" }) {
  // A movement that rounds to nothing is shown as nothing, in no color.
  const delta = raw === undefined ? undefined : Number(raw.toFixed(1));
  const sign = delta === undefined ? "" : delta > 0 ? "+" : delta < 0 ? "−" : "";
  const improved = delta === undefined || !good || delta === 0 ? undefined : (delta > 0) === (good === "up");
  const deltaClass = improved === undefined ? "text-neutral-500" : improved ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-300";
  return (
    <div className={`${surface} p-5`}>
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{label}{hint && <Hint id={hint} label={label.toLowerCase()} />}</div>
      <div className={`mt-2 text-3xl font-semibold tabular-nums ${tone === "warning" ? "text-amber-700 dark:text-amber-300" : "text-ink dark:text-neutral-50"}`}>{value}</div>
      <div className={`mt-1 text-xs ${deltaClass}`}>{delta === undefined ? "no prior window" : `${sign}${Math.abs(delta).toFixed(1)}${deltaUnit} vs prior`}</div>
    </div>
  );
}

function Waits({ totals }: { totals: JourneyDayTotals }) {
  const fmt = useFormat();
  const avg = (s: number, n: number) => (n ? `${fmt.number(Math.round(s / n))}s` : "-");
  return (
    <p className="mt-3 text-xs text-neutral-600 dark:text-neutral-400">
      Answered after {avg(totals.answeredWaitSeconds, totals.answered)} in queue on average; abandoned after {avg(totals.abandonedWaitSeconds, totals.abandoned)}. Handle time {avg(totals.handleSeconds, totals.answered)}. Poor audio on {fmt.count(totals.poorAudio, "contact", "contacts")}.
      {Object.keys(totals.byChannel).length > 1 && <> By channel: {Object.entries(totals.byChannel).map(([c, n]) => `${c.toLowerCase()} ${fmt.number(n)}`).join(", ")}.</>}
    </p>
  );
}

/** Concurrency, folded into Journeys: the last day against the quota, and the way to ask for more (section 8.9). */
function ConcurrencyCard({ environmentId }: { environmentId: string }) {
  const fmt = useFormat();
  const q = useConcurrency(environmentId, 24);
  const quotas = useQuotas(environmentId);
  const calls = q.data?.series.find((s) => s.metric === "ConcurrentCalls");
  const peak = calls?.points.reduce((best, p) => (p.v > best.v ? p : best), { t: "", v: 0 });
  const quota = quotas.data?.quotas.find((x) => /concurrent active calls/i.test(x.quota_name));
  return (
    <Card title="Concurrency vs quota · last 24 hours">
      {q.isPending && <LoadingState />}
      {q.data && !calls && <p className="text-sm text-neutral-600 dark:text-neutral-400">No concurrency points in the last day.</p>}
      {calls && peak && (
        <p className="text-sm">
          Peak <strong>{peak.v}</strong>
          {quota ? ` of ${quota.value}` : ""} concurrent calls{peak.t ? ` at ${fmt.time(peak.t)}` : ""}.
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-3 text-sm">
        <Link to={`/journeys/${environmentId}/concurrency`} className="underline">Every channel</Link>
        <Link to={`/quotas/${environmentId}`} className="underline">Request an increase</Link>
      </div>
    </Card>
  );
}

function RecentContacts({ environmentId, canInspect, traffic }: { environmentId: string; canInspect: boolean; traffic: TrafficKind }) {
  const q = useContactRecords(environmentId, { limit: 6, traffic });
  return (
    <Card title={traffic === "test" ? "Recent test contacts" : "Recent contacts"}>
      {q.isPending && <LoadingState />}
      {q.data && q.data.contacts.length === 0 && <p className="text-sm text-neutral-600 dark:text-neutral-400">Nothing in the last week.</p>}
      {q.data && q.data.contacts.length > 0 && (
        <ul className="divide-y divide-neutral-100 text-sm dark:divide-neutral-800">
          {q.data.contacts.map((c) => (
            <li key={c.contactId} className="flex items-center justify-between gap-3 py-2">
              <span className="min-w-0">
                {canInspect ? <Link to={`/journeys/${environmentId}/contacts/${c.contactId}`} className="font-mono text-xs underline">{c.contactId}</Link> : <span className="font-mono text-xs">{c.contactId}</span>}
                <span className="block text-xs text-neutral-500">
                  <RelativeTime iso={c.initiationTimestamp} />
                  {c.queueName ? ` · ${c.queueName}` : ""}
                  {c.queueSeconds ? ` · waited ${c.queueSeconds}s` : ""}
                </span>
              </span>
              <span className="flex flex-none items-center gap-1">
                {c.test && <TestPill />}
                {c.poorAudio && <StatusPill status="poor audio" tone="warning" />}
                <StatusPill status={OUTCOME_LABEL[c.outcome]} tone={OUTCOME_TONE[c.outcome]} />
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-xs text-neutral-500">
        <Link to={`/journeys/${environmentId}/contacts`} className="underline">All contacts</Link> · flags that say when, how we know and what we think caused it come with the analysis run (ADR-0015 section 5).
      </p>
    </Card>
  );
}
